import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_SERIENDRUCK, SERIENDRUCK_TYP } from "../makros";

export const lektionSeriendruckGrundlagen: Lesson = {
  id: "seriendruck-grundlagen",
  title: "Hauptdokument und Empfängerliste",
  summary: "Ein Brief, eine Liste – und Word erzeugt daraus hundert persönliche Briefe.",
  durationMinutes: 10,
  learningGoals: [
    "Die vier Bausteine eines Seriendrucks kennen",
    "Ein Dokument als Seriendruck-Hauptdokument einrichten",
    "Eine Empfängerliste aus Excel verbinden",
  ],
  steps: [
    {
      id: "idee",
      title: "Die Idee: ein Brief, viele Empfänger",
      explanation: [
        "Ein Seriendruck besteht aus vier Teilen. Das Hauptdokument enthält den Text, " +
          "der für alle gleich ist. Die Datenquelle – meist eine Excel-Tabelle – " +
          "enthält eine Zeile pro Empfänger. Seriendruckfelder im Hauptdokument " +
          "markieren die Stellen, an denen die Angaben aus der Datenquelle erscheinen. Das " +
          "Ergebnis entsteht erst beim Zusammenführen.",
        "Alles dafür steht auf der Registerkarte „Sendungen“, von links nach rechts in der " +
          "Reihenfolge, in der man es braucht: Seriendruck starten, Empfänger auswählen, " +
          "Felder einfügen, Vorschau, Fertigstellen.",
        "Die Datenquelle entscheidet über die Qualität. Eine gute Liste hat genau eine " +
          "Überschriftenzeile, eine Spalte pro Angabe (Anrede, Vorname, Nachname, Straße, " +
          "PLZ, Ort) und keine leeren Zeilen dazwischen – genau das, was der Excel-Kurs " +
          "unter „Tabelle statt Zellhaufen“ lehrt.",
      ],
      keyPoints: [
        "Hauptdokument + Datenquelle + Seriendruckfelder = Ergebnis.",
        "Sendungen: von links nach rechts abarbeiten.",
        "Eine Spalte pro Angabe, eine Zeile pro Empfänger, eine Überschriftenzeile.",
      ],
    },
    {
      id: "hauptdokument",
      title: "Seriendruck starten und Empfänger verbinden",
      explanation: [
        "„Sendungen“ → „Seriendruck starten“ → „Briefe“ macht das offene Dokument zum " +
          "Hauptdokument. Am Text ändert sich dabei nichts.",
        "„Empfänger auswählen“ → „Vorhandene Liste verwenden“ öffnet die Datenquelle. Bei " +
          "einer Excel-Datei fragt Word nach dem Blatt; das Häkchen „Erste Datenzeile enthält " +
          "Spaltenüberschriften“ muss gesetzt sein. Wer keine Liste hat, legt über „Neue " +
          "Liste eingeben“ schnell eine an.",
        "Die Verbindung wird im Hauptdokument gespeichert. Verschiebt oder benennt man die " +
          "Excel-Datei später um, fragt Word beim Öffnen nach ihr.",
      ],
      keyPoints: [
        "Sendungen → Seriendruck starten → Briefe.",
        "Empfänger auswählen → Vorhandene Liste verwenden.",
        "Die Datenquelle nicht verschieben, solange das Hauptdokument sie braucht.",
      ],
      task: {
        instruction:
          "Mach dein Dokument zu einem Seriendruck-Hauptdokument vom Typ „Briefe“ und verbinde " +
          "eine Empfängerliste mit mindestens drei Einträgen.",
        hint: "Sendungen → Seriendruck starten → Briefe, dann Empfänger auswählen → „Neue Liste eingeben“ oder eine Excel-Liste.",
        macro: MAKRO_SERIENDRUCK,
        check: () =>
          pruefeMitMakro(MAKRO_SERIENDRUCK, (bericht) => {
            const typ = bericht.zahl("typ") ?? -1;
            const verbunden = bericht.janein("datenquelle") === true;
            const saetze = bericht.zahl("datensaetze") ?? 0;
            return fromCriteria(
              [
                criterion(`Hauptdokument vom Typ „Briefe“ (erkannt: ${SERIENDRUCK_TYP[typ] ?? typ})`, typ === 0),
                criterion(
                  verbunden ? `Datenquelle verbunden: ${bericht.text("quelle") ?? ""}` : "Eine Datenquelle ist verbunden",
                  verbunden,
                ),
                // Manche Quellen melden die Anzahl erst nach einer Vorschau (-1).
                criterion(
                  `Mindestens drei Empfänger (gemeldet: ${saetze < 0 ? "unbekannt" : saetze})`,
                  saetze >= 3 || (verbunden && saetze < 0),
                ),
              ],
              {
                success: "Hauptdokument und Empfängerliste sind verbunden.",
                failure: "Der Seriendruck ist noch nicht vollständig eingerichtet.",
                hint: "Beide Schritte sind nötig: erst „Seriendruck starten“, dann „Empfänger auswählen“.",
              },
            );
          }),
      },
    },
  ],
};
